import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeMode = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

const STORAGE_KEY = 'bossy-theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';
const THEME_COLOR = { light: '#6366f1', dark: '#0b0f17' } as const;

interface ThemeContextValue {
  /** The user's choice, as persisted in localStorage. */
  mode: ThemeMode;
  /** The theme actually applied: mode itself, or the OS preference in system mode. */
  resolved: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'light' || value === 'dark' || value === 'system';

/**
 * The OS dark-scheme query list, or undefined when matchMedia is unavailable
 * or mocked without an implementation (test environments).
 */
const getDarkQuery = (): MediaQueryList | undefined => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return undefined;
  }
  return window.matchMedia(DARK_QUERY);
};

const systemPrefersDark = (): boolean => !!getDarkQuery()?.matches;

const readStoredMode = (): ThemeMode => {
  if (typeof window === 'undefined') return 'light';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'light';
  } catch {
    // Storage can throw when blocked or in private mode; fall back to light.
    return 'light';
  }
};

/** Toggle the `dark` class and keep native UI colors (color-scheme, theme-color) in sync. */
const applyResolvedTheme = (resolved: ResolvedTheme) => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
  document.documentElement.style.colorScheme = resolved;
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'theme-color');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', THEME_COLOR[resolved]);
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemDark, setSystemDark] = useState<boolean>(systemPrefersDark);

  // In system mode, follow live changes to the OS preference. Tolerates a
  // missing or partial matchMedia result.
  useEffect(() => {
    const media = getDarkQuery();
    if (!media || typeof media.addEventListener !== 'function') return;
    const handleChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    media.addEventListener('change', handleChange);
    return () => {
      if (typeof media.removeEventListener === 'function') {
        media.removeEventListener('change', handleChange);
      }
    };
  }, []);

  const resolved: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    applyResolvedTheme(resolved);
  }, [resolved]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence is best-effort; the in-memory mode still applies.
    }
  }, []);

  const value = useMemo(() => ({ mode, resolved, setMode }), [mode, resolved, setMode]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => {
  const context = useContext(ThemeContext);
  if (context) return context;
  // Outside a provider (e.g. a component rendered in isolation), degrade to a
  // safe default instead of crashing the caller.
  return {
    mode: 'light',
    resolved: 'light',
    setMode: () => undefined,
  };
};
